import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

interface Props {
  zones: readonly Category[];
  activeId?: Category['id'];
}

const BUTTON_CLASS =
  'flex min-w-0 shrink-0 items-center justify-center whitespace-nowrap font-medium text-[14px] leading-none no-underline! transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]';

/**
 * Zone switcher of the DubaiExpress header: one button per top-level
 * category, the open zone filled with the brand color. Centered pills on
 * desktop; on mobile a row under the logo that shares the width while the
 * names fit and scrolls sideways once they do not.
 */
const HeaderDubaiExpressZonesView = ({ zones, activeId }: Props) => {
  const { isMobile } = useGlobal();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    navRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [isMobile, activeId]);

  if (zones.length === 0) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      className={cn(
        isMobile
          ? 'scrollbar-hide flex gap-2 overflow-x-auto px-2 pt-4 pb-2'
          : 'flex items-center justify-center gap-1.5',
      )}
    >
      {zones.map((zone) => {
        const active = zone.id === activeId;

        return (
          <Link
            key={zone.id}
            to={createCategoryUrl({ id: zone.id })}
            aria-current={active ? 'page' : undefined}
            className={cn(
              BUTTON_CLASS,
              isMobile
                ? 'h-8 min-w-max flex-[1_0_0] rounded-[8px] px-3'
                : 'h-10 rounded-[12px] px-5',
              active ? 'bg-[#E94E1A] text-white!' : 'text-[#141415]!',
            )}
          >
            {zone.name}
          </Link>
        );
      })}
    </nav>
  );
};

export const HeaderDubaiExpressZones = observer(HeaderDubaiExpressZonesView);
